export const TRUST_ITEMS = [
  "1,300+ Individual Returns Assisted",
  "Secure Document Handling",
  "Electronic Filing",
  "Personalized Service",
];

export const SERVICES = [
  {
    id: "simple",
    title: "Simple Returns",
    desc: "W-2 income, dependents, retirement income, interest and other common tax situations — prepared carefully, explained plainly.",
    points: ["W-2 wages & withholding", "Interest & dividend income", "Standard deduction optimization"],
    priceItems: [],
  },
  {
    id: "families",
    title: "Families",
    desc: "Dependents, Child Tax Credit, childcare costs, education expenses and the changing situations that come with family life.",
    points: ["Child Tax Credit & dependents", "Childcare & education expenses", "Filing status guidance"],
    priceItems: ["dependents", "dependent-care", "college"],
  },
  {
    id: "self-employed",
    title: "Self-Employed & 1099",
    desc: "Independent contractors, sole proprietors and Schedule C businesses — income, expenses and estimated taxes handled properly.",
    points: ["Schedule C preparation", "Expense categorization", "Quarterly estimated taxes"],
    priceItems: ["self-employed"],
  },
  {
    id: "rental",
    title: "Rental Property Owners",
    desc: "Rental income, expenses, depreciation, property sales and multiple-property situations, reported correctly.",
    points: ["Schedule E preparation", "Depreciation schedules", "Property sale reporting"],
    priceItems: ["rental"],
  },
  {
    id: "investors",
    title: "Investors",
    desc: "Stocks, capital gains and losses, investment income and brokerage statements — reconciled against your 1099-B.",
    points: ["Capital gains & losses", "1099-B reconciliation", "Dividend & interest reporting"],
    priceItems: ["stocks", "interest-dividends"],
  },
  {
    id: "retirement",
    title: "Retirement",
    desc: "Social Security, pensions, IRA distributions and the tax considerations that come with retirement income.",
    points: ["SSA-1099 & pensions", "IRA distributions (1099-R)", "RMD considerations"],
    priceItems: ["retirement"],
  },
  {
    id: "multi-state",
    title: "Multi-State Returns",
    desc: "Lived, worked or earned income in more than one state? We sort out which state gets what — and why.",
    points: ["Part-year resident returns", "Nonresident income allocation", "Credits for taxes paid to other states"],
    priceItems: [],
  },
  {
    id: "prior-year",
    title: "Prior-Year Returns",
    desc: "Behind on filing? It is more common than you think, and it is fixable. We prepare previous years and get you current.",
    points: ["Catch-up filing", "Missing document reconstruction", "A calm, judgment-free process"],
    priceItems: ["prior-year"],
  },
  {
    id: "notices",
    title: "IRS & State Notices",
    desc: "Bring us your tax notice for review. We will explain what it actually says, what it means, and what your options are.",
    points: ["Notice review & plain-English explanation", "Response preparation", "Honest referral if a matter needs representation beyond our authority"],
    priceItems: ["notice"],
  },
];

export const STEPS = [
  { n: "01", title: "Get Started", desc: "Answer a few questions about your tax situation so we know what your return involves." },
  { n: "02", title: "Send Your Documents Securely", desc: "Upload the tax documents necessary to prepare your return through the secure portal — never ordinary email." },
  { n: "03", title: "We Review Everything", desc: "Your information is reviewed for completeness and potential issues before preparation begins." },
  { n: "04", title: "We Ask Questions", desc: "If something is missing, inconsistent or needs clarification, you will hear from us. Questions are part of the process." },
  { n: "05", title: "Your Return Is Prepared", desc: "We prepare the applicable federal and state returns based on the information you provided." },
  { n: "06", title: "Review Before Filing", desc: "You receive an opportunity to review your return and ask questions before authorizing us to file. Nothing is filed until you have seen it.", highlight: true },
  { n: "07", title: "Sign & E-File", desc: "Once approved and properly signed, your return is electronically filed when eligible." },
  { n: "08", title: "Keep Your Records", desc: "You receive a completed copy of your return for your records — yours to keep, always." },
];

export const FAQS = [
  {
    q: "How much does tax preparation cost?",
    a: "Pricing depends on the forms and complexity involved in your return. A W-2-only return generally requires less preparation than a return involving self-employment, rental properties, investments, multiple states or business ownership. Before work begins, we tell you what your return involves and what it will cost — no surprise invoices. Use the Complexity Check for an instant read on your situation, or request a preparation estimate.",
  },
  {
    q: "What documents do I need?",
    a: "It depends on your situation, but most clients need photo ID, Social Security numbers for everyone on the return, income documents (W-2s, 1099s), and records for any deductions or credits. Our interactive document checklist on this page walks you through exactly what applies to you.",
  },
  {
    q: "How long does preparation take?",
    a: "Straightforward returns are typically completed within days once all documents are in. More complex situations — self-employment, rentals, multiple states, prior years — take longer because they deserve more review. We would rather be right than fast, and we will keep you informed either way.",
  },
  {
    q: "Will I see my return before it is filed?",
    a: "Always. You review your return and have the opportunity to ask questions before you authorize filing. Nothing is e-filed until you have seen it, understood the significant items, and signed. The IRS itself advises taxpayers never to sign a blank or incomplete return — we agree, and we go further.",
  },
  {
    q: "When will I get my refund?",
    a: "The IRS issues most e-filed refunds with direct deposit within about 21 days of acceptance, though some returns take longer. Your refund is always deposited to your account — never ours. We will show you how to track it after filing.",
  },
  {
    q: "Do you file electronically?",
    a: "Yes. Returns are e-filed when eligible, which means faster processing, confirmation of receipt, and fewer errors than paper filing.",
  },
  {
    q: "I haven't filed in a few years. Can you help?",
    a: "Yes. Prior-year returns are a regular part of what we do. We will help you figure out which years need filing, reconstruct missing documents where possible, and get you current — without judgment.",
  },
  {
    q: "I received an IRS or state notice. What should I do?",
    a: "Do not ignore it — most notices have deadlines. Bring it to us and we will review it, explain in plain language what it says, and outline your options. If a matter requires representation beyond what our credentials permit, we will tell you directly and point you toward the right professional.",
  },
  {
    q: "How is my information protected?",
    a: "Sensitive documents move through a secure client portal rather than ordinary email. Accounts are protected with multi-factor authentication where available, access to client information is limited to what is necessary to provide services, and we maintain written information security procedures — the same Written Information Security Plan the IRS requires of tax professionals.",
  },
  {
    q: "Do I need to visit an office?",
    a: "No. The entire process — questionnaire, document upload, questions, review and signing — can be handled remotely. If you prefer to meet, consultations can be scheduled by appointment.",
  },
  {
    q: "What happens if I have questions after filing season?",
    a: "We are still here after April 15. Questions about a filed return, a notice that arrives in July, or planning for next year do not have a season. Year-round availability is part of the service.",
  },
  {
    q: "Will you promise me a big refund?",
    a: "No — and be cautious of any preparer who does before reviewing your information. We do not promise particular refunds, invent deductions, or base advice on what someone supposedly got away with last year. We prepare accurate returns and claim every deduction and credit that legitimately applies. Your refund belongs to you.",
  },
];

export const CHECKLIST_GROUPS = [
  {
    id: "personal",
    title: "Personal information",
    items: [
      { t: "Photo ID" },
      { t: "Social Security numbers / ITIN information for everyone on the return" },
      { t: "Prior-year tax return (new clients)" },
      { t: "Bank account & routing numbers for direct deposit" },
      { t: "Dates of birth for everyone on the return" },
    ],
  },
  {
    id: "income",
    title: "Income",
    items: [
      { t: "W-2 — wages from employers", fee: "w2" },
      { t: "1099-NEC — self-employment / contract work", fee: "self-employed" },
      { t: "1099-MISC — other income" },
      { t: "1099-INT — interest income", fee: "interest-dividends" },
      { t: "1099-DIV — dividend income", fee: "interest-dividends" },
      { t: "1099-B — stock & brokerage sales", fee: "stocks" },
      { t: "1099-R — retirement distributions", fee: "retirement" },
      { t: "SSA-1099 — Social Security benefits" },
      { t: "K-1s — partnership / S-corp / trust income", fee: "k1" },
      { t: "1099-G — unemployment or state refunds", fee: "unemployment" },
      { t: "Cryptocurrency transaction records", fee: "crypto" },
    ],
  },
  {
    id: "deductions",
    title: "Deductions & credits",
    items: [
      { t: "Childcare expenses & provider details", fee: "dependent-care" },
      { t: "Education forms (1098-T) & student loan interest (1098-E)", fee: "college" },
      { t: "Mortgage interest (1098) & property taxes", fee: "itemized" },
      { t: "Charitable contributions", fee: "itemized" },
      { t: "Business expenses (self-employed)", fee: "self-employed" },
      { t: "Rental property expenses", fee: "rental" },
      { t: "Estimated tax payments made" },
      { t: "Home sale documents", fee: "home-sale" },
      { t: "Energy-efficiency improvement records", fee: "energy-home" },
    ],
  },
];

// cat: "income" (income & forms), "deduction", "credit", "other" (situations)
export const COMPLEXITY_ITEMS = [
  { id: "w2", label: "W-2 income", cat: "income" },
  { id: "retirement", label: "Retirement income", cat: "income" },
  { id: "unemployment", label: "Unemployment income (1099-G)", cat: "income" },
  { id: "interest-dividends", label: "Interest & dividends", cat: "income" },
  { id: "stocks", label: "Stock sales", cat: "income" },
  { id: "crypto", label: "Cryptocurrency", cat: "income" },
  { id: "gambling", label: "Gambling winnings (W-2G)", cat: "income" },
  { id: "self-employed", label: "Self-employment / 1099", cat: "income" },
  { id: "rental", label: "Rental property", cat: "income" },
  { id: "k1", label: "K-1 income", cat: "income" },
  { id: "home-sale", label: "Home sale", cat: "income" },
  { id: "royalties", label: "Royalty income (Schedule E)", cat: "income" },
  { id: "farm", label: "Farm income (Schedule F)", cat: "income" },
  { id: "foreign-income", label: "Foreign income (FEIE / FBAR)", cat: "income" },
  { id: "multi-state", label: "Multiple states", cat: "income" },
  { id: "itemized", label: "Itemized deductions / homeowner", cat: "deduction" },
  { id: "hsa", label: "HSA (1099-SA / 8889)", cat: "deduction" },
  { id: "student-loan", label: "Student loan interest (1098-E)", cat: "deduction" },
  { id: "ira-deduction", label: "IRA contributions", cat: "deduction" },
  { id: "educator", label: "Educator expenses", cat: "deduction" },
  { id: "dependents", label: "Dependents (Child Tax Credit)", cat: "credit" },
  { id: "dependent-care", label: "Child/dependent care credit", cat: "credit" },
  { id: "college", label: "Education credits", cat: "credit" },
  { id: "eitc", label: "Earned Income Credit (EITC)", cat: "credit" },
  { id: "saver-credit", label: "Saver's Credit", cat: "credit" },
  { id: "energy-home", label: "Home energy credits", cat: "credit" },
  { id: "ev-credit", label: "Electric vehicle credit (EV)", cat: "credit" },
  { id: "adoption", label: "Adoption credit", cat: "credit" },
  { id: "foreign-tax", label: "Foreign tax credit", cat: "credit" },
  { id: "elderly-disabled", label: "Elderly / disabled credit", cat: "credit" },
  { id: "marketplace", label: "Marketplace health insurance (1095-A)", cat: "credit" },
  { id: "prior-year", label: "Prior-year returns", cat: "other" },
  { id: "amended", label: "Amended return", cat: "other" },
  { id: "notice", label: "IRS / state notice", cat: "other" },
];

// Hover-card copy for the pricing grid: who typically qualifies + the key limitation to know going in.
export const ITEM_INFO = {
  "w2": {
    eligibility: "Anyone who received a W-2 from an employer during the year.",
    limitations: "Covers wage income only — freelance or contract income needs a 1099, not a W-2.",
  },
  "retirement": {
    eligibility: "Received distributions from a 401(k), IRA, pension or annuity (Form 1099-R).",
    limitations: "Withdrawals before age 59½ may trigger a 10% early-withdrawal penalty unless an exception applies.",
  },
  "unemployment": {
    eligibility: "Received unemployment compensation from a state workforce agency (Form 1099-G).",
    limitations: "Unemployment benefits are fully taxable at the federal level — no special exclusion currently applies.",
  },
  "interest-dividends": {
    eligibility: "Received interest (1099-INT) or dividends (1099-DIV) from banks, brokerages or investments.",
    limitations: "Qualified dividends get capital-gains rates; ordinary interest and dividends are taxed at your regular rate.",
  },
  "stocks": {
    eligibility: "Sold stocks, ETFs, mutual funds or other securities during the year (1099-B).",
    limitations: "Requires cost-basis records; gains held one year or less are taxed as ordinary income.",
  },
  "crypto": {
    eligibility: "Bought, sold, traded or received cryptocurrency during the year.",
    limitations: "Every disposal — including trading one coin for another — is a taxable event that needs its own record.",
  },
  "gambling": {
    eligibility: "Won gambling income from a casino, lottery or sports betting (often reported on W-2G).",
    limitations: "Winnings are fully taxable; losses are only deductible if you itemize, and only up to the amount you won.",
  },
  "self-employed": {
    eligibility: "Freelance, contract, gig or sole-proprietor income (1099-NEC/1099-K) — including single-member LLCs.",
    limitations: "Subject to self-employment tax (~15.3%) on top of income tax; expense records are needed to claim deductions.",
  },
  "rental": {
    eligibility: "Own and rent out real estate — long-term or short-term.",
    limitations: "Losses may be limited by passive-activity rules unless you actively participate or qualify as a real-estate professional.",
  },
  "k1": {
    eligibility: "You're a partner, S-corp shareholder or trust beneficiary who received a Schedule K-1.",
    limitations: "We prepare your personal return using the K-1 you receive — we do not prepare the entity's own return.",
  },
  "home-sale": {
    eligibility: "Sold your primary residence or other real property during the year.",
    limitations: "Up to $250k ($500k married filing jointly) of gain on a primary home may be excluded if ownership/use tests are met.",
  },
  "royalties": {
    eligibility: "Received royalty income from books, patents, mineral rights or similar sources (1099-MISC).",
    limitations: "Reported on Schedule E; related expenses may be deductible against the royalty income.",
  },
  "farm": {
    eligibility: "Operate a farm or receive farm income (Schedule F).",
    limitations: "Special rules apply for income averaging and estimated-tax timing unique to farm income.",
  },
  "foreign-income": {
    eligibility: "Earned income abroad or hold foreign financial accounts.",
    limitations: "The Foreign Earned Income Exclusion has residency/physical-presence tests; FBAR is required if foreign accounts exceeded $10,000 at any point.",
  },
  "multi-state": {
    eligibility: "Lived or worked in more than one state during the year.",
    limitations: "Each state filed adds its own fee — New York and California returns are not supported by this office.",
  },
  "itemized": {
    eligibility: "Mortgage interest, property taxes, charitable giving or medical expenses that add up to more than the standard deduction.",
    limitations: "Only worth it if your itemized total beats the standard deduction; the SALT deduction is capped at $10,000.",
  },
  "hsa": {
    eligibility: "Contributed to or took distributions from a Health Savings Account (Form 8889/1099-SA).",
    limitations: "Requires HDHP coverage to contribute; non-qualified withdrawals before 65 are taxed plus a 20% penalty.",
  },
  "student-loan": {
    eligibility: "Paid interest on a qualified student loan (Form 1098-E).",
    limitations: "Deduction is capped at $2,500/year and phases out at higher incomes.",
  },
  "ira-deduction": {
    eligibility: "Contributed to a traditional IRA.",
    limitations: "The deduction may be limited or phased out if you (or a spouse) are covered by a workplace retirement plan.",
  },
  "educator": {
    eligibility: "K-12 educator who paid for classroom supplies out of pocket.",
    limitations: "Capped at $300 ($600 if both spouses are educators) — keep your receipts.",
  },
  "dependents": {
    eligibility: "Claiming a qualifying child or relative as a dependent.",
    limitations: "The Child Tax Credit requires the child be under 17 with a valid SSN; it phases out above roughly $200k single / $400k married.",
  },
  "dependent-care": {
    eligibility: "Paid for childcare or dependent care so you (and a spouse) could work or look for work.",
    limitations: "Expenses are capped at $3,000 for one dependent / $6,000 for two or more, and the provider's tax ID is required.",
  },
  "college": {
    eligibility: "Paid qualified tuition or education expenses (1098-T) for yourself or a dependent.",
    limitations: "The American Opportunity Credit is limited to the first four years of study; both credits phase out at higher incomes.",
  },
  "eitc": {
    eligibility: "Low-to-moderate earned income, with or without qualifying children.",
    limitations: "Income and investment-income limits apply, and the amount varies significantly by filing status and number of children.",
  },
  "saver-credit": {
    eligibility: "Contributed to a 401(k) or IRA and have moderate income.",
    limitations: "Phases out at relatively low income levels; not available to full-time students or dependents.",
  },
  "energy-home": {
    eligibility: "Installed qualifying energy-efficient home improvements — windows, insulation, heat pumps, solar and similar.",
    limitations: "Credit percentage and annual/lifetime caps vary by improvement type; the home generally must be your primary residence.",
  },
  "ev-credit": {
    eligibility: "Purchased a qualifying new or used electric or plug-in hybrid vehicle.",
    limitations: "Income limits, vehicle price caps and battery/assembly sourcing rules apply — not every EV qualifies.",
  },
  "adoption": {
    eligibility: "Paid qualified adoption expenses for an eligible child.",
    limitations: "The credit is nonrefundable with an annual cap that phases out at higher incomes.",
  },
  "foreign-tax": {
    eligibility: "Paid income tax to a foreign country on foreign-source income.",
    limitations: "The credit is generally capped at the U.S. tax attributable to that income; Form 1116 may be required.",
  },
  "elderly-disabled": {
    eligibility: "You (or a spouse) are 65+ or permanently and totally disabled, with limited income.",
    limitations: "Strict income and nontaxable-benefit thresholds mean this applies to relatively few filers.",
  },
  "marketplace": {
    eligibility: "Purchased health coverage through the ACA Marketplace (Form 1095-A).",
    limitations: "Advance premium tax credits must be reconciled — you may owe money back if your actual income came in higher than estimated.",
  },
  "prior-year": {
    eligibility: "Need to file a return for a year you missed.",
    limitations: "Refunds generally can't be claimed more than 3 years after the original due date; penalties and interest may apply on balances due.",
  },
  "amended": {
    eligibility: "Need to correct a return that's already been filed and accepted.",
    limitations: "Must generally be filed within 3 years of the original filing to claim an additional refund.",
  },
  "notice": {
    eligibility: "Received a letter from the IRS or a state tax agency.",
    limitations: "Response deadlines on notices are firm — bring it to us as soon as you receive it.",
  },
};

// Grouped display of pricing/complexity selections so filers can tell
// income forms, deductions and credits apart while building their estimate.
export const COMPLEXITY_CATEGORIES = [
  { id: "income", label: "Income & forms", hint: "The income and forms that make up your return" },
  { id: "deduction", label: "Deductions", hint: "Amounts that reduce your taxable income" },
  { id: "credit", label: "Credits", hint: "Dollar-for-dollar reductions of the tax you owe" },
  { id: "other", label: "Other situations", hint: "Prior-year, amended returns and notices" },
];

// Shown above the selectable items so filers know extra forms outside this list are still covered
export const FORMS_CHARGED_NOTE =
  "The situations below are what carry an additional fee. Every other form required to complete your return — beyond what's listed here — is already included in the base preparation fee.";

// Prominent, site-wide scope warning — we are an individual/personal tax practice only
export const INDIVIDUAL_ONLY_NOTE_POSITIVE =
  "We prepare individual (personal) tax returns only — including Schedule C for sole proprietors and single-member LLCs, filed on your personal return.";
export const INDIVIDUAL_ONLY_NOTE_NEGATIVE =
  "We do not prepare Partnership (Form 1065), S-Corporation (Form 1120-S), C-Corporation (Form 1120), Trust or Estate (Form 1041) returns.";

export const COMPLEXITY_TIERS = [
  {
    level: 1,
    name: "Straightforward",
    desc: "Your situation looks like a relatively simple return. Preparation is typically quick and at the lower end of the fee range.",
  },
  {
    level: 2,
    name: "Moderate",
    desc: "Your return involves a few moving parts. Expect some additional forms and a moderate preparation fee.",
  },
  {
    level: 3,
    name: "Complex",
    desc: "Your return may require additional schedules or review. We will walk you through exactly what is involved before any work begins.",
  },
  {
    level: 4,
    name: "Highly complex",
    desc: "Your situation combines several advanced areas. It deserves careful preparation — and a clear, quoted fee before we start.",
  },
];

export const STANDARDS = [
  { n: "01", title: "Accuracy Over Speed", desc: "Getting the return right matters more than racing it out the door. A fast mistake is still a mistake." },
  { n: "02", title: "Questions Are Part of the Process", desc: "If information appears incomplete or inconsistent, we will ask about it. That is not suspicion — it is diligence." },
  { n: "03", title: "You Get to Ask Questions Too", desc: "No one should feel embarrassed asking why something appears on their return. If we cannot explain it, it should not be there." },
  { n: "04", title: "No Made-Up Deductions", desc: "We claim deductions and credits supported by the tax law and the information you provide. Nothing invented, nothing inflated." },
  { n: "05", title: "Review Before Filing", desc: "Nothing should be a surprise when you are asked to authorize filing. You see the return first — always." },
  { n: "06", title: "Confidentiality Matters", desc: "Tax information is handled as the sensitive financial information it is — secure portal, controlled access, written security procedures." },
  { n: "07", title: "We're Still Here After April 15", desc: "Notices, questions and planning do not follow a filing calendar. Neither do we." },
];

export const PATHS = [
  {
    id: "file",
    label: "File My Taxes",
    title: "Individual tax preparation, explained clearly.",
    desc: "From simple W-2 returns to investments, rentals and multi-state situations — prepared carefully, reviewed with you before filing.",
    bullets: ["Secure document upload", "Review before filing — always", "Federal & state e-file"],
    cta: { label: "Explore Tax Services", href: "/tax-services" },
  },
  {
    id: "self-employed",
    label: "I'm Self-Employed",
    title: "Schedule C, expenses and estimated taxes — handled.",
    desc: "Contractors, freelancers and sole proprietors: we prepare your return, help you understand deductible expenses, and get estimated payments under control.",
    bullets: ["1099-NEC & Schedule C", "Expense guidance", "Quarterly estimated taxes"],
    cta: { label: "Self-Employed Returns", href: "/tax-services" },
  },
  {
    id: "business",
    label: "Sole Prop / LLC Owner",
    title: "Schedule C prep and books for solo business owners.",
    desc: "Tax preparation and bookkeeping for sole proprietors and single-member LLCs, filed on your personal return. We prepare individual returns only — no partnership, S-corp, C-corp, trust or estate filings.",
    bullets: ["Schedule C tax prep", "Bookkeeping & records", "Year-round availability"],
    cta: { label: "Business Services", href: "/business-services" },
  },
  {
    id: "problem",
    label: "I Have a Tax Problem",
    title: "A notice, or years unfiled? Start here.",
    desc: "Bring the notice, or tell us which years are missing. We will review it, explain what it means in plain language, and lay out your options.",
    bullets: ["IRS & state notice review", "Prior-year returns", "Honest assessment, no judgment"],
    cta: { label: "Get It Reviewed", href: "/contact" },
  },
];

export const BUSINESS_SERVICES = [
  {
    title: "Sole Prop & LLC Tax Returns",
    desc: "Preparation of your Schedule C business income and expenses as part of your personal tax return — for sole proprietors and single-member LLCs. We do not prepare partnership, S-corporation, C-corporation, trust or estate returns.",
    cta: "See Schedule C pricing",
    to: "/pricing?items=self-employed",
  },
  {
    title: "Bookkeeping & Records",
    desc: "Clean, current books make tax season boring — in the best way. Monthly or quarterly bookkeeping that keeps your numbers reliable and your deductions documented.",
    cta: "Ask about bookkeeping",
    to: "/book",
  },
  {
    title: "Estimated Taxes & Planning",
    desc: "No more April ambushes. We project what you will owe and set up quarterly payments that keep penalties away and cash flow predictable.",
    cta: "Plan my quarterlies",
    to: "/book",
  },
  {
    title: "New Business Guidance",
    desc: "Just starting out as a sole proprietor or single-member LLC? We'll walk you through the tax side of getting set up — registrations, records to keep, and habits that pay off at filing time.",
    cta: "Get set up right",
    to: "/book",
  },
];

export const PORTAL_BASICS = [
  "Photo ID",
  "Social Security numbers / ITINs for everyone on the return",
  "Prior-year tax return (new clients)",
  "Bank account & routing numbers for direct deposit",
  "Dates of birth for everyone on the return",
];

export const INCOME_TYPES = [
  { id: "w2", label: "W-2 job", docs: ["W-2 from each employer"] },
  { id: "self-employed", label: "Self-employment / 1099", docs: ["1099-NEC / 1099-K forms", "Business expense records", "Estimated tax payments made"] },
  { id: "investments", label: "Investments", docs: ["1099-B & brokerage statements", "1099-INT (interest)", "1099-DIV (dividends)"] },
  { id: "retirement", label: "Retirement income", docs: ["1099-R (IRA / pension distributions)"] },
  { id: "social-security", label: "Social Security", docs: ["SSA-1099"] },
  { id: "rental", label: "Rental property", docs: ["Rental income & expense records", "Purchase & improvement documents"] },
  { id: "k1", label: "K-1 received", docs: ["Schedule K-1s"] },
  { id: "unemployment", label: "Unemployment", docs: ["1099-G"] },
  { id: "crypto", label: "Cryptocurrency", docs: ["Crypto transaction records / exchange reports"] },
];

export const UNSUPPORTED_STATES = ["NY", "CA"];

export const NO_TAX_STATES = ["AK", "FL", "NV", "NH", "SD", "TN", "TX", "WA", "WY"];

export const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" }, { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" }, { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" }, { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" }, { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" }, { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" }, { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" }, { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" }, { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" }, { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" }, { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" },
  { code: "RI", name: "Rhode Island" }, { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" }, { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" }, { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" }, { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];
